import { useFunctionalityAccess } from '../Providers/FunctionalityAccessProvider';
import { FEATURE_CODES } from '../Services/functionalityAccess';

/**
 * Read `is_admin` from the cached profile blob in localStorage. The new
 * /auth/v2/token/verify response sets this; older sessions may not have it.
 * @returns {boolean}
 */
const readIsAdminFromProfile = () => {
  try {
    const raw = localStorage.getItem('profile');
    if (!raw) return false;
    const profile = JSON.parse(raw);
    return profile?.is_admin === true;
  } catch (_) {
    return false;
  }
};

/**
 * Custom hook for admin access control
 * Provides utilities to check and manage admin permissions
 */
export const useAdminAccess = () => {
  const { hasFeature, getFeatureStatus, initialized } = useFunctionalityAccess();

  /**
   * Check if user has admin access. Prefers the `is_admin` claim from the
   * profile (new auth-lambda response); falls back to the FunctionalityAccess
   * gate for sessions that predate the new claim.
   * @returns {boolean}
   */
  const hasAdminAccess = () => {
    if (readIsAdminFromProfile()) return true;
    if (!initialized) return false;
    return hasFeature(FEATURE_CODES.ADMIN);
  };

  /**
   * Get admin feature status details
   * @returns {Object} Admin feature status information
   */
  const getAdminStatus = () => {
    if (!initialized) {
      return {
        hasAccess: false,
        status: 'unknown',
        endDate: null,
        metaInfo: null,
      };
    }
    return getFeatureStatus(FEATURE_CODES.ADMIN);
  };

  /**
   * Check if admin access is active
   * @returns {boolean} True if admin access is active
   */
  const isAdminActive = () => {
    const status = getAdminStatus();
    return status.hasAccess && status.status === 'active';
  };

  return {
    hasAdminAccess,
    getAdminStatus,
    isAdminActive,
    initialized,
    FEATURE_CODES,
  };
};
